import { useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import BordOneToneIcon from '../../NudosComponents/BordOneToneIcon/BordOneToneIcon';
import BordAcordeon from '../../BordAcordeon/BordAcordeon';
import { IsidebarModule } from '../SidebarModules';
import { bordColorTheme, fgDefault } from 'styles/styles.constants';
import { isModuleActive } from '../../../views/LogisticServices/utils/general.utils';
import useSidebarState from '../../../state/useSidebarState';
import './ExpandableSidebarModule.scss';

/**
 * Componente que renderiza un módulo del sidebar con capacidad de expandirse
 * y mostrar sub-módulos mediante un acordeón
 */
const ExpandableSidebarModule = ({
  mainModule,
  subModules,
  selectedModule,
  isActive,
  defaultExpanded = false,
  onModuleClick,
  onSubModuleClick
}: ExpandableSidebarModuleProps) => {
  const filteredSubModules = useMemo(() => {
    return subModules?.filter(subModule => subModule?.show && isModuleActive(subModule?.name));
  }, [subModules]);

  const isAnySubModuleSelected = filteredSubModules?.some(sub => sub?.name === selectedModule);
  const { setExpandedModule, expandedModules } = useSidebarState();
  
  const isExpanded = useMemo(() => {
    if (isAnySubModuleSelected) {
      return true;
    }
    const persistedExpanded = expandedModules[mainModule?.name];
    return persistedExpanded !== undefined ? persistedExpanded : defaultExpanded;
  }, [isAnySubModuleSelected, expandedModules, mainModule?.name, defaultExpanded]);

  const isMainModuleSelected = selectedModule === mainModule?.name;
  const inactiveStyles = isActive ? '' : 'inactive';
  const selectedStyles = isMainModuleSelected ? 'selectItem' : '';
  const expandedStyles = isExpanded ? 'expanded' : '';

  useEffect(() => {
    if (isAnySubModuleSelected) {
      setExpandedModule(mainModule?.name, true);
    }
  }, [isAnySubModuleSelected, mainModule?.name, setExpandedModule]);

  const handleMainModuleClick = () => {
    if (!isActive) return;
    const newExpandedState = !isExpanded;
    setExpandedModule(mainModule?.name, newExpandedState);
    if (mainModule?.route) {
      onModuleClick(mainModule);
    }
  };

  const handleSubModuleClick = (subModule: IsidebarModule) => {
    if (!isActive) return;
    onSubModuleClick(subModule);
  };

  return (
    <div className="expandableSidebarModule">
      <div
        className={`sidebarModuleMenu ${selectedStyles} ${inactiveStyles} ${expandedStyles}`}
        onClick={handleMainModuleClick}
      >
        <div className="moduleIcon">{mainModule.img}</div>
        <div className="moduleTitle">{mainModule.title}</div>
        <div className="expandIcon">
          <BordOneToneIcon
            variant="arrowHeadDown"
            customWidth="10px"
            stroke={isMainModuleSelected ? bordColorTheme.white[50] : fgDefault}
          />
        </div>
      </div>

      {filteredSubModules.length > 0 && (
        <BordAcordeon
          isExpanded={isExpanded}
          customClassName="subModulesContainer"
          customContentClassName="subModulesContent"
        >
          {filteredSubModules.map(subModule => {
            const isSubModuleSelected = selectedModule === subModule.name;
            const subModuleSelectedStyles = isSubModuleSelected ? 'selectItem' : '';
            const isSubModuleActive = isModuleActive(subModule?.name);

            return (
              <div key={uuid()} className="sidebarSubModuleWrapper">
                <div
                  className={`sidebarSubModule ${subModuleSelectedStyles} ${!isSubModuleActive ? 'inactive' : ''}`}
                  onClick={() => handleSubModuleClick(subModule)}
                >
                  <div className="subModuleIcon">{subModule.img}</div>
                  <div className="subModuleTitle">{subModule.title}</div>
                </div>
              </div>
            );
          })}
        </BordAcordeon>
      )}
    </div>
  );
};

interface ExpandableSidebarModuleProps {
  mainModule: IsidebarModule;
  subModules: IsidebarModule[];
  selectedModule: string;
  isActive: boolean;
  defaultExpanded?: boolean;
  onModuleClick: (module: IsidebarModule) => void;
  onSubModuleClick: (subModule: IsidebarModule) => void;
}

export default ExpandableSidebarModule;
